import { supabase } from '../../../../services/supabase';

interface TeamMember {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export type DetailedTeam = {
  id: string;
  name: string;
  work_hours_start: string;
  work_hours_end: string;
  check_in_times: string[];
  team_members: TeamMember[];
  team_lead: TeamMember;
  account_manager: TeamMember;
};

export async function fetchTeamDetails(teamId: string): Promise<DetailedTeam> {
  if (!teamId) {
    throw new Error('Team ID is required');
  }

  // Get basic team info
  const { data: teamData, error: teamError } = (await supabase
    .from('teams')
    .select('*')
    .eq('id', teamId)
    .single()) as {
    data: {
      id: string;
      name: string;
      work_hours_start: string;
      work_hours_end: string;
      check_in_times: string[];
      team_members: string[];
      manager_id: string;
      account_manager_id: string;
    };
    error: Error | null;
  };

  if (teamError)
    throw new Error(`Failed to fetch team details: ${teamError.message}`);
  if (!teamData) throw new Error('Team not found');

  // Get all relevant users in one query
  const userIds = [
    ...teamData.team_members,
    ...(teamData.manager_id ? [teamData.manager_id] : []),
    ...(teamData.account_manager_id ? [teamData.account_manager_id] : []),
  ];
  // console.log(teamData, userIds);
  const { data: usersData, error: usersError } = await supabase
    .from('users')
    .select('id, first_name, last_name, email')
    .in('id', userIds);

  if (usersError)
    throw new Error(`Failed to fetch users: ${usersError.message}`);

  // Sort users into their respective roles
  const teamMembers = usersData.filter((user: TeamMember) =>
    teamData.team_members.includes(user.id),
  );
  const teamLead = usersData.find((user) => user.id === teamData.manager_id);
  const accountManager = usersData.find(
    (user) => user.id === teamData.account_manager_id,
  );

  return {
    ...teamData,
    team_members: teamMembers,
    team_lead: teamLead || ({} as TeamMember),
    account_manager: accountManager || ({} as TeamMember),
  };
}
